// GET /api/selfcare/balance — HAQIQIY backend kontrakti
// (`xsib-core/selfcare/internal/BalanceController.java`).
//
// ⚠️ IKKI XIL "trafik" bor, ikkalasi ham qaytadi va CHALKASHTIRMASLIK kerak:
//   · `trafficLedgerBytes` — `ledger.account` dagi XOM qoldiq. Bu UMUMIY,
//     kumulyativ hisob (barcha grantlardan kelgan kredit minus sarflangan) —
//     "qancha oldim, qancha ishlatdim" degan tarixiy yig'indi.
//   · `activeGrant` — AMALDAGI BITTA paket holati (eng erta tugaydigani).
//     Abonent aslida "hozirgi paketimdan qancha qoldi?" deb so'raydi —
//     shu savolga FAQAT shu maydon javob beradi. `null` — faol paket yo'q.
//
// Dashboard UI shuning uchun ASOSAN `activeGrant` ni ko'rsatadi (5.7-band:
// "Balans — pul va qolgan trafik, katta va aniq"), `trafficLedgerBytes` esa
// ikkinchi darajali/qo'shimcha ma'lumot sifatida.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import { toDeliveryMethod, type DeliveryMethod } from '@/shared/auth/authApi'
import { money, type Money } from '@/shared/lib/money'

const moneySchema = z.object({ amount: z.number(), currency: z.string() })

const activeGrantSchema = z.object({
  grantId: z.string(),
  deliveryMethod: z.string(),
  quotaBytes: z.number().nullish(),
  consumedBytes: z.number(),
  remainingBytes: z.number().nullish(),
  quotaSeconds: z.number().nullish(),
  consumedSeconds: z.number(),
  remainingSeconds: z.number().nullish(),
  // ⚠️ `nullish` — grant MUDDATSIZ bo'lishi mumkin va bu odatiy holat.
  // Backend'da `Instant expiresAt` nullable (`BalanceResponse.ActiveGrantView`),
  // Jackson esa `null` maydonni javobga umuman qo'shmaydi.
  //
  // Bu yerda ilgari majburiy `z.string()` turardi va oqibati og'ir edi:
  // parse yiqilardi → TanStack Query qayta urardi → CHEKSIZ SIKL, kabinet
  // abadiy "loading" da qolardi. Abonent hech qanday xato ko'rmasdi.
  // Qolgan null bo'la oladigan maydonlarning HAMMASI `nullish`, faqat shu
  // bittasi unutilgan edi.
  expiresAt: z.string().nullish(),
})

const balanceSchema = z.object({
  money: moneySchema,
  trafficLedgerBytes: z.number(),
  activeGrant: activeGrantSchema.nullish(),
  totalRemaining: z.object({
    bytes: z.number().nullish(),
    seconds: z.number().nullish(),
    packets: z.number(),
  }).nullish(),
})

export interface ActiveGrant {
  grantId: string
  deliveryMethod: DeliveryMethod
  /** `null` — hajm bo'yicha cheksiz */
  quotaBytes: number | null
  consumedBytes: number
  /** `null` — hajm bo'yicha cheksiz (`quotaBytes` bilan bir xil holatda) */
  remainingBytes: number | null
  /** `null` — vaqt bo'yicha cheksiz */
  quotaSeconds: number | null
  consumedSeconds: number
  /** `null` — vaqt bo'yicha cheksiz */
  remainingSeconds: number | null
  /** `null` — MUDDATSIZ paket. UI'da sana o'rniga chiziqcha ko'rsatiladi. */
  expiresAt: string | null
}

export interface Balance {
  money: Money
  /** Ledger'dagi xom, kumulyativ trafik qoldig'i — ikkinchi darajali ko'rsatkich */
  trafficLedgerBytes: number
  /** `null` — hozir amaldagi paket yo'q (abonentga paket sotib olish taklif qilinadi) */
  activeGrant: ActiveGrant | null
  /**
   * BARCHA amaldagi paketlarning qoldig'i.
   *
   * ⚠️ `activeGrant` BITTA paket (hozir sarflanayotgani). Abonent esa
   * ekrandagi raqamni UMUMIY qoldiq deb o'qiydi — 3 x 5 GB olgan
   * abonent 5 GB ko'rib "paketim qayerda?" deb so'rardi.
   *
   * `bytes: null` — CHEKSIZ. `0` esa "tugagan" — ikkalasi boshqa.
   */
  totalRemaining: {
    bytes: number | null
    seconds: number | null
    /** Nechta paket qo'shildi — yig'indi qayerdan kelgani ko'rinsin */
    packets: number
  } | null
}

export async function fetchBalance(): Promise<Balance> {
  const response = await api.get<unknown>('/api/selfcare/balance')
  const parsed = balanceSchema.parse(response.data)
  const grant = parsed.activeGrant

  return {
    money: money(parsed.money.amount, parsed.money.currency),
    trafficLedgerBytes: parsed.trafficLedgerBytes,
    activeGrant: grant
      ? {
          grantId: grant.grantId,
          deliveryMethod: toDeliveryMethod(grant.deliveryMethod),
          quotaBytes: grant.quotaBytes ?? null,
          consumedBytes: grant.consumedBytes,
          remainingBytes: grant.remainingBytes ?? null,
          quotaSeconds: grant.quotaSeconds ?? null,
          consumedSeconds: grant.consumedSeconds,
          remainingSeconds: grant.remainingSeconds ?? null,
          expiresAt: grant.expiresAt ?? null,
        }
      : null,
    totalRemaining: parsed.totalRemaining
      ? {
          bytes: parsed.totalRemaining.bytes ?? null,
          seconds: parsed.totalRemaining.seconds ?? null,
          packets: parsed.totalRemaining.packets,
        }
      : null,
  }
}
